import { type ConsoleOverview, type PlatformAbility, platformCan } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, Outlet, useRouterState } from '@tanstack/react-router';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import { api } from '../../lib/api';
import { Page, PageHeader } from '../../ui/layout';
import { usePlatformRole } from './console-shared';
import { overviewQuery } from './shared';

const TABS: ReadonlyArray<{
  to: string;
  title: string;
  ability: PlatformAbility;
  match: (p: string) => boolean;
}> = [
  {
    to: '/platform',
    title: 'Компании',
    ability: 'companies',
    match: (p: string) => p === '/platform' || p.startsWith('/platform/tenants'),
  },
  { to: '/platform/users', title: 'Люди', ability: 'users', match: (p) => p.startsWith('/platform/users') },
  {
    to: '/platform/security',
    title: 'Безопасность',
    ability: 'security',
    match: (p) => p.startsWith('/platform/security'),
  },
  {
    to: '/platform/announcements',
    title: 'Объявления',
    ability: 'announcements',
    match: (p) => p.startsWith('/platform/announcements'),
  },
  {
    to: '/platform/plans',
    title: 'Тарифы',
    ability: 'billing',
    match: (p) => p.startsWith('/platform/plans'),
  },
  {
    to: '/platform/requests',
    title: 'Заявки на доступ',
    ability: 'billing',
    match: (p) => p.startsWith('/platform/requests'),
  },
  {
    to: '/platform/invoices',
    title: 'Счета',
    ability: 'billing',
    match: (p) => p.startsWith('/platform/invoices'),
  },
  {
    to: '/platform/markets',
    title: 'Страны и рынки',
    ability: 'billing',
    match: (p) => p.startsWith('/platform/markets'),
  },
  {
    to: '/platform/sales',
    title: 'Продажи',
    ability: 'billing',
    match: (p) => p.startsWith('/platform/sales'),
  },
  {
    to: '/platform/team',
    title: 'Команда портала',
    ability: 'team',
    match: (p) => p.startsWith('/platform/team'),
  },
  {
    to: '/platform/events',
    title: 'Журнал',
    ability: 'companies',
    match: (p) => p.startsWith('/platform/events'),
  },
  {
    to: '/platform/pilot',
    title: 'Pilot',
    ability: 'billing',
    match: (p) => p.startsWith('/platform/pilot'),
  },
  {
    to: '/platform/versions',
    title: 'Версии',
    ability: 'billing',
    match: (p) => p.startsWith('/platform/versions'),
  },
];

/**
 * Кабинет портала: компании, люди всех компаний, безопасность входа, объявления (ЛЦ-09…ЛЦ-13), а владельцу
 * портала ещё тарифы, заявки, счета, рынки, продажи, команда портала и Pilot (ТЗ 6.29–6.30, ЛЦ-01…ЛЦ-05).
 */
export function PlatformLayout() {
  const role = usePlatformRole();
  const owner = role === 'owner';
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const overview = useQuery({ ...overviewQuery, enabled: owner });
  const consoleQ = useQuery({
    queryKey: ['platform', 'console', 'overview'],
    queryFn: () => api<ConsoleOverview>('/platform/console/overview'),
    enabled: Boolean(role) && !owner,
  });
  const nav = useRef<HTMLElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: прокрутка — при смене раздела, активный ищется в разметке
  useEffect(() => {
    const el = nav.current;
    const active = el?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!el || !active) return;
    el.scrollLeft = Math.max(0, active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2);
  }, [pathname]);

  // Не в команде портала — кабинета нет: ссылка из закладки ведёт на главную
  if (!role) return <Navigate to="/" replace />;
  const tabs = TABS.filter((t) => platformCan(role, t.ability));
  // Раздел только для владельца портала по прямой ссылке — на список компаний
  const current = TABS.find((t) => t.match(pathname));
  if (current && !platformCan(role, current.ability)) return <Navigate to="/platform" replace />;

  const showStrip = !pathname.startsWith('/platform/tenants/') && !pathname.startsWith('/platform/users/');
  const tiles = owner
    ? overview.data
      ? [
          {
            label: 'Компаний',
            value: overview.data.tenants,
            note: `${overview.data.active} с действующей лицензией`,
            tone: '',
          },
          {
            label: 'Заканчиваются',
            value: overview.data.expiring,
            note: 'в ближайшие 30 дней или в льготном периоде',
            tone: overview.data.expiring ? 'text-accent-text' : '',
          },
          {
            label: 'Только чтение',
            value: overview.data.readonly,
            note: `${overview.data.suspended} приостановлено владельцем`,
            tone: overview.data.readonly || overview.data.suspended ? 'text-danger' : '',
          },
          {
            label: 'Новых заявок',
            value: overview.data.newRequests,
            note: `${overview.data.plans} тарифов в продаже`,
            tone: overview.data.newRequests ? 'text-accent-text' : '',
          },
        ]
      : null
    : consoleQ.data
      ? [
          {
            label: 'Компаний',
            value: consoleQ.data.companies,
            note: `людей в портале: ${consoleQ.data.people}`,
            tone: '',
          },
          {
            label: 'Работали за 7 дней',
            value: consoleQ.data.active7,
            note: 'люди компаний',
            tone: '',
          },
          {
            label: 'Неудачных входов',
            value: consoleQ.data.failed24h,
            note: 'за 24 часа',
            tone: consoleQ.data.failed24h ? 'text-accent-text' : '',
          },
          {
            label: 'Блокировки',
            value: consoleQ.data.activeLocks + consoleQ.data.blockedUsers,
            note: `перебор: ${consoleQ.data.activeLocks}, вход закрыт: ${consoleQ.data.blockedUsers}`,
            tone: consoleQ.data.activeLocks ? 'text-danger' : '',
          },
        ]
      : null;
  return (
    <Page>
      <PageHeader
        title={owner ? 'Владелец портала' : 'Администрирование портала'}
        subtitle={
          owner
            ? 'Все компании портала: люди и доступы, вход в компанию, безопасность, объявления; тарифы, лицензии, счета и продажи.'
            : 'Все компании портала: люди и доступы, вход в компанию без сумм, безопасность входа и объявления.'
        }
      />
      {/* Сводка — на списках; в карточке компании и человека она только отодвигает содержимое.
          На телефоне — две колонки, чтобы вкладки были видны без прокрутки */}
      {tiles && showStrip ? (
        <dl className="mb-6 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-line bg-line md:grid-cols-4">
          {tiles.map((it) => (
            <div key={it.label} className="flex min-w-0 flex-col gap-0.5 bg-surface px-4 py-3">
              <dt className="text-[13px] text-ink-2">{it.label}</dt>
              <dd
                className={clsx(
                  'num text-[24px] leading-8 font-semibold tracking-[-0.01em]',
                  it.tone || 'text-ink',
                )}
              >
                {it.value}
              </dd>
              <dd className="text-[12px] leading-4 text-ink-2">{it.note}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      <nav
        ref={nav}
        className="-mt-2 mb-6 flex gap-1 overflow-x-auto border-b border-line"
        aria-label="Разделы кабинета портала"
      >
        {tabs.map((t) => {
          const active = t.match(pathname);
          return (
            <Link
              key={t.to}
              to={t.to}
              aria-current={active ? 'page' : undefined}
              className={clsx(
                'relative -mb-px flex h-11 items-center px-3 text-[14px] whitespace-nowrap md:h-10',
                active ? 'border-b-2 border-ink font-semibold text-ink' : 'text-ink-2 hover:text-ink',
              )}
            >
              {t.title}
            </Link>
          );
        })}
      </nav>
      <Outlet />
    </Page>
  );
}
